import styled from "@emotion/styled";
import { Chip, Popover, Stack, Typography } from "@mui/material";
import {
  Coins,
  DotsThreeOutlineVertical,
  PencilLine,
  Trash,
  X
} from "@phosphor-icons/react";
import { LoadingScreen } from "components/LoadingScreen";
import { Button, Notification } from "components/UI";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import moment from "moment";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { useDeleteSettlement, useExpense, useSettlements } from "../api";
import {
  CreateAdvanceSettlementModal,
  UpdateAdvanceSettlementModal
} from "../components";
import { useExpenseStore } from "../store";

const Container = styled.div`
  padding-block: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f1f5f9;
  padding: 18px 20px;
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  outline: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;

  th {
    padding-block: 16px;
    background: #e6f7f8;
    font-weight: 400;
  }

  & td {
    text-align: center;
    padding: 10px 0;
    border-bottom: 1px solid #d4d4d4;
  }
`;

const Banner = styled.div`
  border-radius: 2px;
  background: #f8bc77;
  width: 391px;
  height: 83px;
  color: #fff;
  padding: 0 20px;
  display: flex;
  align-items: center;
`;

const BannerTitle = styled.span`
  font-size: 12px;
  font-weight: 500;
`;

const Amount = styled.span`
  font-size: 12px;
`;

const Figure = styled.span`
  font-size: 32px;
  font-weight: 700;
`;

const InformationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  font-size: 12px;
  width: 30%;
`;

const Information = styled.li`
  display: flex;
  align-items: center;
  gap: 16px;

  span {
    color: #778899;
  }
`;

const MoreIcon = styled(DotsThreeOutlineVertical)`
  cursor: pointer;
`;

export const ExpenseSettlement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const openCreateAdvanceSettlementModal = useExpenseStore(
    state => state.openCreateAdvanceSettlementModal
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const expenseQuery = useExpense({
    churchId: selectedChurch?.id,
    expenseId: id
  });
  const settlementsQuery = useSettlements({
    churchId: selectedChurch?.id,
    expenseId: id
  });
  const [settlement, setSettlement] = useState(null);
  const [receiptToView, setReceiptToView] = useState("");

  const handleClick = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSettlement(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (expenseQuery.isLoading || settlementsQuery.isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Container>
      <CreateAdvanceSettlementModal />
      <UpdateAdvanceSettlementModal data={settlement} />
      <Notification />
      <Header>
        <Title>Expense Settlement</Title>
        <CloseButton onClick={() => navigate(`/finances/temp/expense`)}>
          <span>Close</span>
          <X size={16} />
        </CloseButton>
      </Header>
      <Banner>
        <Stack flex={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <BannerTitle>Advance to clear</BannerTitle>
            <Coins
              size={32}
              style={{
                mixBlendMode: "soft-light"
              }}
            />
          </Stack>
          <Amount>
            <Figure>
              {Number(
                expenseQuery.data?.result?.advance_to_clear
              ).toLocaleString()}
            </Figure>{" "}
            ETB
          </Amount>
        </Stack>
      </Banner>
      <InformationList>
        <Information>
          <span>Requestor Full Name</span>
          <strong>
            {expenseQuery.data?.result?.requisition?.user?.name ||
              expenseQuery.data?.result?.requisition_record?.user?.name ||
              expenseQuery.data?.result?.requisition_record?.requested_by}
          </strong>
        </Information>
        <Information>
          <span>Department</span>
          <strong>
            {expenseQuery.data?.result?.requisition?.team?.name ||
              expenseQuery.data?.result?.requisition_record?.team?.name}
          </strong>
        </Information>
        <Information>
          <span>Amount</span>
          <strong>
            {Number(
              expenseQuery.data?.result?.requisition?.amount ||
                expenseQuery.data?.result?.requisition_record?.amount
            ).toLocaleString()}{" "}
            ETB
          </strong>
        </Information>
        <Information>
          <span>Reason</span>
          <strong>
            {expenseQuery.data?.result?.requisition?.reason ||
              expenseQuery.data?.result?.requisition_record?.reason}
          </strong>
        </Information>
        <Information>
          <span>Category</span>
          <strong>
            {expenseQuery.data?.result?.requisition?.chart_of_account
              ?.account_name ||
              expenseQuery.data?.result?.requisition_record?.chart_of_account
                ?.account_name}
          </strong>
        </Information>
        <Information>
          <span>Account</span>
          <strong>
            {expenseQuery.data?.result?.requisition?.chart_of_account
              ?.account_name ||
              expenseQuery.data?.result?.requisition_record?.chart_of_account
                ?.account_name}
          </strong>
        </Information>
      </InformationList>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Title>Expenses</Title>
        <Button size="small" onClick={openCreateAdvanceSettlementModal}>
          Add Settlement
        </Button>
      </Stack>
      <Table>
        <thead>
          <tr>
            <th>Reason</th>
            <th>Amount</th>
            <th>Chart Of Account</th>
            <th>Receipt Photo</th>
            <th>Date</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {settlementsQuery.data?.result?.map(settlement => (
            <tr key={settlement.id}>
              <td>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                >
                  <span>{settlement.reason}</span>
                  <Chip
                    size="small"
                    label={settlement.settlement_status}
                    color={
                      settlement.settlement_status?.toLowerCase() === "approved"
                        ? "success"
                        : settlement.settlement_status?.toLowerCase() ===
                          "rejected"
                        ? "error"
                        : "warning"
                    }
                  />
                </Stack>
              </td>
              <td>{Number(settlement.amount).toLocaleString()}</td>
              <td>{settlement.chart_of_account?.account_name}</td>
              <td>
                <Button
                  flavor="white"
                  style={{
                    fontSize: "12px"
                  }}
                  onClick={() => setReceiptToView(settlement.receipt_photo)}
                >
                  View Receipt
                </Button>
              </td>
              <td>{moment(settlement.created_at).format("DD-MM-YYYY")}</td>
              <td>
                {settlement.settlement_status?.toLowerCase() === "approved" ||
                settlement.settlement_status?.toLowerCase() ===
                  "rejected" ? null : (
                  <MoreIcon
                    size={24}
                    weight="fill"
                    onClick={e => handleClick(e, settlement)}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Popover
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 2
        }}
      >
        <ExpensesDropdown expenseId={id} data={settlement} />
      </Popover>

      <Lightbox
        open={!!receiptToView}
        close={() => setReceiptToView("")}
        slides={[{ src: `${IMAGE_URL}/${receiptToView}` }]}
      />
    </Container>
  );
};

const ExpensesDropdown = ({ expenseId, data }) => {
  const openUpdateAdvanceSettlementModal = useExpenseStore(
    state => state.openUpdateAdvanceSettlementModal
  );
  const deleteSettlementMutation = useDeleteSettlement();
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const deleteSettlement = () => {
    deleteSettlementMutation.mutate({
      churchId: selectedChurch?.id,
      expenseId: expenseId,
      settlementId: data?.id
    });
  };

  return (
    <Stack
      sx={{
        minWidth: "200px",
        backgroundColor: "white",
        p: 1,
        gap: 1
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          p: 1,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "neutral.main"
          }
        }}
        onClick={openUpdateAdvanceSettlementModal}
      >
        <PencilLine size={16} color="#000" />
        <Typography variant="subtitle2">Edit</Typography>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          p: 1,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "neutral.main"
          }
        }}
        onClick={deleteSettlement}
      >
        <Trash size={16} color="#000" />
        <Typography variant="subtitle2">Delete</Typography>
      </Stack>
    </Stack>
  );
};
